import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { usePublicSettings } from "@/contexts/SettingsContext";
import LinkedText from "@/components/LinkedText";
import RelatedServices from "@/components/RelatedServices";

const NAVY = "#1E3A8A";

function CtaButton({ text, link, variant = "primary" }) {
  if (!text) return null;
  const cls =
    variant === "primary"
      ? "inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors"
      : "inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-300 text-[#0F172A] font-semibold hover:bg-slate-50 transition-colors";
  const isInternal = link && link.startsWith("/");
  const isTel = link && link.startsWith("tel:");
  const inner = (
    <>
      {isTel && <Phone className="w-4 h-4" />}
      {text}
      {variant === "primary" && !isTel && <ArrowRight className="w-4 h-4" />}
    </>
  );
  if (isInternal) return <Link to={link} className={cls}>{inner}</Link>;
  return <a href={link || "#"} className={cls}>{inner}</a>;
}

function Hero({ hero, seen }) {
  if (!hero) return null;
  return (
    <section className="bg-white">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="font-display font-bold text-3xl lg:text-5xl leading-tight text-[#0F172A]">
            {hero.h1}
          </h1>
          {hero.supporting_text && (
            <LinkedText text={hero.supporting_text} seen={seen} className="mt-5 text-lg text-[#475569] leading-relaxed" />
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <CtaButton text={hero.primary_cta_text} link={hero.primary_cta_link} variant="primary" />
            <CtaButton text={hero.secondary_cta_text} link={hero.secondary_cta_link} variant="secondary" />
          </div>
        </div>
        {hero.image_data ? (
          <div className="rounded-2xl overflow-hidden shadow-soft">
            <img src={hero.image_data} alt={hero.image_alt || hero.h1 || ""} className="w-full h-full object-cover" />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function TextImage({ data, seen }) {
  const side = data.image_side === "left" ? "lg:flex-row-reverse" : "";
  return (
    <section className="py-14 border-t border-slate-100">
      <div className={`max-w-6xl mx-auto px-4 lg:px-8 flex flex-col lg:flex-row gap-10 items-center ${side}`}>
        <div className="flex-1">
          {data.heading && (
            <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A]">{data.heading}</h2>
          )}
          <LinkedText text={data.body} seen={seen} className="mt-4 text-[#475569] leading-relaxed whitespace-pre-line" />
          {data.cta_text && (
            <div className="mt-6">
              <CtaButton text={data.cta_text} link={data.cta_link} variant="primary" />
            </div>
          )}
        </div>
        {data.image_data ? (
          <div className="flex-1 rounded-2xl overflow-hidden shadow-soft">
            <img src={data.image_data} alt={data.image_alt || data.heading || ""} className="w-full h-full object-cover" />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ServiceCards({ data, seen }) {
  const items = data.items || [];
  return (
    <section className="py-14 border-t border-slate-100 bg-[#F8FAFC]">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {data.heading && (
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A] mb-8">{data.heading}</h2>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <div key={it.id || i} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6">
              {it.image_data ? (
                <div className="mb-4 rounded-xl overflow-hidden aspect-[16/10]">
                  <img src={it.image_data} alt={it.image_alt || ""} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5 text-[#1E3A8A]" />
                </div>
              )}
              <h3 className="font-display font-semibold text-lg text-[#0F172A]">{it.title}</h3>
              <LinkedText text={it.description} seen={seen} className="mt-2 text-sm text-[#475569] leading-relaxed" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks({ data, seen }) {
  const items = data.items || [];
  return (
    <section className="py-14 border-t border-slate-100">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {data.heading && (
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A] mb-8">{data.heading}</h2>
        )}
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <div key={it.id || i} className="relative bg-white rounded-2xl border border-slate-100 shadow-soft p-6">
              <div className="w-10 h-10 rounded-full bg-[#1E3A8A] text-white font-bold flex items-center justify-center mb-4">
                {i + 1}
              </div>
              <h3 className="font-display font-semibold text-lg text-[#0F172A]">{it.title}</h3>
              <LinkedText text={it.description} seen={seen} className="mt-2 text-sm text-[#475569] leading-relaxed" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq({ data, seen }) {
  const items = data.items || [];
  return (
    <section className="py-14 border-t border-slate-100 bg-[#F8FAFC]">
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A] mb-8">
          {data.heading || "Frequently Asked Questions"}
        </h2>
        <div className="space-y-4">
          {items.map((it, i) => (
            <div key={it.id || i} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6">
              <h3 className="font-display font-semibold text-[#0F172A]">{it.question}</h3>
              <LinkedText text={it.answer} seen={seen} className="mt-2 text-sm text-[#475569] leading-relaxed whitespace-pre-line" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ data, seen }) {
  return (
    <section className="py-16 border-t border-slate-100">
      <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
        <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A]">
          {data.heading || "Get a Free Quote"}
        </h2>
        <LinkedText text={data.text} seen={seen} className="mt-4 text-[#475569] leading-relaxed" />
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <CtaButton text={data.primary_cta_text} link={data.primary_cta_link} variant="primary" />
          <CtaButton text={data.secondary_cta_text} link={data.secondary_cta_link} variant="secondary" />
        </div>
      </div>
    </section>
  );
}

function PhotoGallery({ data }) {
  const items = (data.items || []).filter((it) => it.image_data);
  if (!items.length && !data.heading) return null;
  return (
    <section className="py-14 border-t border-slate-100">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {data.heading && (
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A] mb-8">{data.heading}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <div key={it.id || i} className="rounded-2xl overflow-hidden shadow-soft aspect-square">
              <img src={it.image_data} alt={it.image_alt || ""} loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeforeAfter({ data }) {
  const items = data.items || [];
  return (
    <section className="py-14 border-t border-slate-100 bg-[#F8FAFC]">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {data.heading && (
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A] mb-8">{data.heading}</h2>
        )}
        <div className="space-y-8">
          {items.map((it, i) => (
            <div key={it.id || i}>
              <div className="grid sm:grid-cols-2 gap-4">
                <figure className="relative rounded-2xl overflow-hidden shadow-soft aspect-[4/3] bg-slate-100">
                  <span className="absolute top-3 left-3 z-10 bg-[#0F172A]/80 text-white text-xs font-bold px-2 py-1 rounded">Before</span>
                  {it.before_image_data ? (
                    <img src={it.before_image_data} alt={it.before_alt || ""} loading="lazy" className="w-full h-full object-cover" />
                  ) : null}
                </figure>
                <figure className="relative rounded-2xl overflow-hidden shadow-soft aspect-[4/3] bg-slate-100">
                  <span className="absolute top-3 left-3 z-10 bg-[#1E3A8A] text-white text-xs font-bold px-2 py-1 rounded">After</span>
                  {it.after_image_data ? (
                    <img src={it.after_image_data} alt={it.after_alt || ""} loading="lazy" className="w-full h-full object-cover" />
                  ) : null}
                </figure>
              </div>
              {it.caption && <p className="mt-3 text-sm text-center text-[#475569]">{it.caption}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ data }) {
  const items = data.items || [];
  return (
    <section className="py-14 border-t border-slate-100">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {data.heading && (
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A] mb-8">{data.heading}</h2>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <figure key={it.id || i} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6">
              <blockquote className="text-[#475569] leading-relaxed whitespace-pre-line">“{it.quote}”</blockquote>
              <figcaption className="mt-4 flex items-center gap-3">
                {it.image_data ? (
                  <img src={it.image_data} alt={it.image_alt || ""} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
                ) : null}
                {it.name && <span className="font-display font-semibold text-[#0F172A]">{it.name}</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceAreas({ data, seen }) {
  const items = data.items || [];
  return (
    <section className="py-14 border-t border-slate-100 bg-[#F8FAFC]">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {data.heading && (
          <h2 className="font-display font-bold text-2xl lg:text-3xl text-[#0F172A] mb-6">{data.heading}</h2>
        )}
        {data.image_data ? (
          <div className="mb-6 rounded-2xl overflow-hidden shadow-soft max-h-80">
            <img src={data.image_data} alt={data.image_alt || ""} loading="lazy" className="w-full h-full object-cover" />
          </div>
        ) : null}
        <LinkedText text={data.description} seen={seen} className="text-[#475569] leading-relaxed mb-6 whitespace-pre-line" />
        <div className="flex flex-wrap gap-2">
          {items.map((it, i) => (
            <div key={it.id || i} className="bg-white rounded-xl border border-slate-100 shadow-soft px-4 py-2">
              <span className="font-medium text-[#0F172A]">{it.name}</span>
              {it.description ? <span className="text-sm text-[#475569]"> — {it.description}</span> : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionRenderer({ section, seen }) {
  const d = section.data || {};
  switch (section.type) {
    case "text_image": return <TextImage data={d} seen={seen} />;
    case "service_cards": return <ServiceCards data={d} seen={seen} />;
    case "how_it_works": return <HowItWorks data={d} seen={seen} />;
    case "faq": return <Faq data={d} seen={seen} />;
    case "cta": return <CtaSection data={d} seen={seen} />;
    case "photo_gallery": return <PhotoGallery data={d} />;
    case "before_after": return <BeforeAfter data={d} />;
    case "testimonials": return <Testimonials data={d} />;
    case "service_areas": return <ServiceAreas data={d} seen={seen} />;
    default: return null;
  }
}

function applySeo(page, settings) {
  if (!page) return;
  const title = page.seo_title || page.name || "P & A Cleaning";
  document.title = title;
  setMeta("description", page.meta_description || "");
  setLinkRel("canonical", page.canonical_url || "");
  setMeta("robots", page.index_page === false ? "noindex,nofollow" : "index,follow");

  // Service structured data from existing business info
  const provider = {
    "@type": "LocalBusiness",
    name: settings.business_name || "P & A Cleaning",
  };
  if (settings.phone) provider.telephone = settings.phone;
  if (settings.service_area) provider.areaServed = settings.service_area;
  const ld = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.name,
    serviceType: page.name,
    provider,
    url: page.canonical_url || "",
  };
  if (settings.service_area) ld.areaServed = settings.service_area;
  let tag = document.getElementById("ld-landing");
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = "ld-landing";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(ld);
}

function setMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}
function setLinkRel(rel, href) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function LandingPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const previewId = params.get("preview");
  const settings = usePublicSettings();

  const { data: page, isPending, isError, error } = useQuery({
    queryKey: ["landing-page", previewId ? `preview:${previewId}` : `slug:${slug}`],
    queryFn: async () => {
      const res = previewId
        ? await api.get(`/admin/landing-pages/${previewId}`)
        : await api.get(`/public/landing/${slug}`);
      return res.data;
    },
    retry: false,
  });

  useEffect(() => {
    if (page) {
      try { applySeo(page, settings || {}); } catch (_) { /* SEO best-effort */ }
    }
  }, [page, settings]);

  if (isPending) {
    return <div data-testid="lp-loading-v2" className="max-w-6xl mx-auto px-4 py-24 text-center text-[#475569]">Loading…</div>;
  }
  if (isError && error?.response?.status === 401) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display font-bold text-2xl text-[#0F172A]">Sign in to preview</h1>
        <p className="mt-3 text-[#475569]">Please log into the admin dashboard to preview an unpublished page.</p>
        <Link to="/admin/login" className="mt-6 inline-block text-[#1E3A8A] font-semibold hover:underline">Go to admin login →</Link>
      </div>
    );
  }
  if (isError || !page) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display font-bold text-3xl text-[#0F172A]">Page not found</h1>
        <p className="mt-3 text-[#475569]">The page you are looking for isn’t available.</p>
        <Link to="/" className="mt-6 inline-block text-[#1E3A8A] font-semibold hover:underline">Back to home →</Link>
      </div>
    );
  }

  const sections = (page.sections || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  // Each destination is linked once per page in body copy (avoids overlinking).
  const seen = new Set();
  // Public API returns [{label, url}] filtered to live pages; admin preview gets related_links_preview.
  const related = previewId
    ? page.related_links_preview || []
    : (page.related_links || []).filter((l) => l && typeof l === "object");
  // Place Related Services right before the final CTA section, otherwise at the end.
  const lastIdx = sections.length - 1;
  const relatedBefore = lastIdx >= 0 && sections[lastIdx].type === "cta" ? lastIdx : -1;
  const relatedBlock = <RelatedServices links={related} testId="lp-related-services" />;
  return (
    <div data-testid={`landing-page-${page.slug}`}>
      {previewId && (
        <div className="bg-amber-100 text-amber-900 text-sm text-center py-2 px-4">
          Preview mode — status: <strong>{page.status}</strong>. This is not the public live view.
        </div>
      )}
      <Hero hero={page.hero} seen={seen} />
      {sections.map((s, i) => (
        <div key={s.id} className="contents">
          {i === relatedBefore && relatedBlock}
          <SectionRenderer section={s} seen={seen} />
        </div>
      ))}
      {relatedBefore === -1 && relatedBlock}
    </div>
  );
}
